import { initials } from '@concord/domain';
import { CONSOLE_NAV, NavShell, PhaseTag, Rail, SearchBox, TopBar } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../../lib/api';
import { type Me, useSignOut } from '../../lib/session';

/**
 * The navigator console frame: rail, top bar with search, page content. `current` marks
 * the rail item; `roomy` is the dashboard's spacing.
 */
export function ConsoleLayout({
  me,
  current,
  roomy,
  counts,
  children,
}: {
  me: Me;
  current: string;
  roomy?: boolean;
  counts?: Record<string, number | null>;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const signOut = useSignOut();
  const [q, setQ] = useState('');
  // Until a required authenticator is set up, the console answers only the setup.
  const locked =
    (!!me.authenticator?.required && !me.authenticator.enabled) ||
    (!!me.authenticator?.enabled && !me.authenticator.verified);
  const rail = useQuery({
    queryKey: ['cases', 'rail-counts'],
    queryFn: () => api<{ counts: Record<string, number> }>('/staff/cases?limit=1'),
    staleTime: 30_000,
    enabled: !locked,
  });
  const superAdmin = me.permissions.includes('admin.super');
  const fb = useQuery({
    queryKey: ['feedback', 'new'],
    queryFn: () => api<{ newCount: number }>('/staff/feedback?filter=new'),
    staleTime: 60_000,
    enabled: superAdmin && !locked,
  });
  const railCounts = {
    ...(counts ?? {
      cases: rail.data?.counts.all ?? null,
      panels: rail.data?.counts.panel ?? null,
      consultations: rail.data?.counts.week ?? null,
    }),
  };
  const newFeedback = fb.data?.newCount ?? 0;
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <NavShell
      roomy={roomy}
      rail={<Rail groups={CONSOLE_NAV} current={current} counts={railCounts} permissions={me.permissions} />}
      topbar={
        <TopBar
          search={
            <SearchBox
              inputRef={searchRef}
              value={q}
              onChange={setQ}
              onSubmit={() => q.trim() && navigate(`/console/search?q=${encodeURIComponent(q.trim())}`)}
            />
          }
          user={{ initials: initials(me), name: `${me.firstName} ${me.lastName}`, role: me.roleLabel }}
          menu={[
            { label: 'Security', onSelect: () => navigate('/console/security') },
            ...(me.docsUrl
              ? [
                  {
                    label: 'User guide',
                    hint: 'opens in a new tab',
                    onSelect: () => window.open(me.docsUrl!, '_blank', 'noopener'),
                  },
                ]
              : []),
            ...(superAdmin
              ? [
                  {
                    label: 'Feedback',
                    hint: newFeedback ? `${newFeedback} new` : undefined,
                    onSelect: () => navigate('/console/feedback'),
                    separatorBefore: true,
                  },
                  { label: 'Audit log', onSelect: () => navigate('/console/audit') },
                  { label: 'Configuration', hint: <PhaseTag phase="P5" />, disabled: true },
                ]
              : []),
            { label: 'Sign out', onSelect: () => signOut('/console/sign-in'), separatorBefore: true },
          ]}
        />
      }
    >
      {children}
    </NavShell>
  );
}
